'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CalendarToday,
  MyLocation,
  AltRoute,
  People,
  ZoomOutMap,
  AssignmentInd,
  Business,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  VerifiedUser,
  FileDownload,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = ['Schedule', 'Scope', 'Select Focus Areas', 'Build the Sample', 'Assign the Team'];

const painPoints = [
  'Mock surveys that happen sporadically, with no schedule or defined scope',
  'Resident samples chosen by convenience instead of survey-style criteria',
  'No designated mock survey team—whoever\'s available walks the halls',
  'Focus areas picked by gut feel rather than mapped survey risk',
  'Practice that skips the Critical Element Pathways surveyors will actually use',
  'No record of what was planned or covered, so the next mock survey starts from scratch',
];

const capabilities = [
  {
    icon: CalendarToday,
    title: 'Structured Mock Survey Scheduling',
    description: 'Create each mock survey with a title, survey type, status, and a defined start and end window—so survey practice is a scheduled program with a record, not an improvised walkthrough.',
  },
  {
    icon: MyLocation,
    title: 'Survey Scope Definition',
    description: 'Set the scope level for each exercise and target specific units—facility-wide reviews, focused unit reviews, or anything between—so the team knows exactly where the mock survey will look.',
  },
  {
    icon: AltRoute,
    title: 'Focus Areas Mapped to Pathways',
    description: 'Select the focus areas the mock survey will examine, and CareAscendium pulls in the Critical Element Pathways mapped to those areas—the same pathway-to-focus-area mappings used across the platform\'s survey readiness tools.',
  },
  {
    icon: People,
    title: 'CMS-Style Resident Sampling Criteria',
    description: 'Build the resident sample the way a survey team would: set the initial sample size, include complaint and facility-reported-incident residents, pull in new admissions from the last 30 days, and target residents with specific conditions.',
  },
  {
    icon: ZoomOutMap,
    title: 'Survey-Realistic Expansion Rules',
    description: 'Document expansion criteria in the plan—expand the sample if substandard quality of care or immediate jeopardy is identified, and sample similar compliant cases for comparison—mirroring how real surveys widen their lens.',
  },
  {
    icon: AssignmentInd,
    title: 'Team Assignment',
    description: 'Assign the mock survey team by naming specific team members or assigning entire roles—so the exercise has designated reviewers, not volunteers of the moment.',
  },
  {
    icon: Business,
    title: 'Plan Management by Facility',
    description: 'Manage every facility\'s mock surveys in one place—edit plans as dates and teams shift, track status, keep comments on the plan, and deactivate exercises that are superseded.',
  },
];

const roles = [
  {
    title: 'Administrators',
    benefit: 'A scheduled mock survey program with defined scope and named accountability',
  },
  {
    title: 'Survey readiness leaders',
    benefit: 'CMS-style methodology—focus areas, pathways, and sampling—built into every plan',
  },
  {
    title: 'DONs & clinical leaders',
    benefit: 'Resident samples built on survey criteria, including high-risk and new-admission groups',
  },
  {
    title: 'Mock survey team members',
    benefit: 'Clear assignment, defined scope, and the pathways the exercise will work',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Consistent mock survey planning and cadence across every facility',
  },
];

const integrations = [
  'Critical Element Pathways',
  'Survey Management',
  'Action Plans',
  'Audits',
  'Multi-Facility Management',
];

const outcomes = [
  { value: 'Paced', label: 'Program cadence', detail: 'Mock surveys scheduled with defined windows instead of run ad hoc' },
  { value: 'True', label: 'Methodology fidelity', detail: 'CMS-style scope, sampling, and expansion criteria on every plan' },
  { value: 'Deep', label: 'Focus-area coverage', detail: 'Which focus areas and pathways each exercise examined' },
  { value: 'Clear', label: 'Team accountability', detail: 'Named members or roles assigned to every mock survey' },
  { value: 'Logged', label: 'Planning history', detail: 'A managed record of every exercise—scope, sample, team, and status' },
  { value: 'Ready', label: 'Readiness connection', detail: 'Pathways and corrective actions linked to the broader readiness program' },
];

const complianceItems = [
  'Scheduled mock surveys with defined types, windows, and statuses',
  'Scope and unit targeting documented on every plan',
  'Focus areas tied to the Critical Element Pathways surveyors use',
  'Resident sampling criteria modeled on the CMS survey process—including complaint and FRI residents, new admissions, and specific conditions',
  'Expansion rules for substandard quality of care and immediate jeopardy scenarios',
  'Named team accountability for every exercise',
];

const faqs = [
  {
    q: 'How is this different from just walking the building?',
    a: 'A CareAscendium mock survey is planned like a real one: a defined window, documented scope and units, focus areas with their mapped Critical Element Pathways, CMS-style resident sampling criteria, and a named team—so the exercise rehearses actual survey methodology.',
  },
  {
    q: 'How is the resident sample built?',
    a: 'Set an initial sample size and apply survey-style criteria: include complaint and facility-reported-incident residents, new admissions from the last 30 days, and residents with specific conditions. Plans can also document expansion rules for SQC or immediate jeopardy findings and comparison sampling of compliant cases.',
  },
  {
    q: 'How do Critical Element Pathways fit in?',
    a: 'When you select focus areas for the mock survey, the pathways mapped to those areas are pulled into the plan. Teams then run those pathways as assigned, tracked reviews through the platform\'s CEP workflow.',
  },
  {
    q: 'Who conducts the mock survey?',
    a: 'The team you assign—specific members or entire roles—named directly on the plan so accountability is explicit.',
  },
  {
    q: 'Can we run mock surveys across multiple facilities?',
    a: 'Yes. Mock surveys are planned and managed per facility, so multi-site operators can maintain a consistent program and cadence across the portfolio.',
  },
  {
    q: 'What happens with what the mock survey finds?',
    a: 'Pathway reviews completed during the exercise are tracked in the CEP workflow, and anything that needs fixing can become an assigned, due-dated action plan in the platform\'s corrective action workflow.',
  },
];

export default function MockSurveysClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Mock Survey Planning Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Mock Surveys"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Plan Mock Surveys the Way CMS Plans Real Ones—Scope, Sample, Team, and Pathways
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            CareAscendium lets skilled nursing facilities schedule internal mock surveys with real survey methodology—defined scope and focus areas, CMS-style resident sampling criteria, an assigned survey team, and the Critical Element Pathways the review will work.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>

          </Box>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={10} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Ad-Hoc Survey Practice
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Most facilities know they should run mock surveys—but without structure, &ldquo;mock survey&rdquo;
              means a walkthrough whenever leadership has a free afternoon. The scope is improvised,
              the resident sample is whoever's convenient, no team is formally assigned, and the exercise
              looks nothing like what a real survey team will actually do.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={10}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A mock survey that doesn't mirror real survey methodology rehearses the wrong test.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Plan. Built on Real Survey Methodology.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              CareAscendium Mock Surveys structure your internal survey exercises the way CMS structures real ones.
              Define the survey type and window, set the scope down to specific units, choose focus areas with their
              mapped Critical Element Pathways, build a resident sample using CMS-style criteria, and assign a named
              team—all in one managed plan per facility.
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: 'white' }}>
            <Stepper
              alternativeLabel
              activeStep={-1}
              sx={{
                '& .MuiStepLabel-label': { fontWeight: 600, color: BRAND_BLUE, mt: 1 },
                '& .MuiStepIcon-root': { color: BRAND_BLUE, '&.Mui-completed': { color: BRAND_GREEN } },
              }}
            >
              {workflowSteps.map((label) => (
                <Step key={label} completed>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, mt: 4, gap: 2, textAlign: 'center' }}>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Schedule:</b> Create the mock survey with a title, type, status, and dates
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Scope:</b> Set scope levels and target specific units for review
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Select Focus Areas:</b> Choose focus areas and pull mapped pathways
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Build the Sample:</b> Define resident sampling criteria modeled on CMS
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', px: 1 }}>
                 <b>Assign the Team:</b> Name team members or roles, and manage to completion
               </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the CareAscendium Platform
                </Typography>
                <Typography sx={{ opacity: 0.9, mb: 3, lineHeight: 1.6 }}>
                  Mock Surveys sit inside a complete survey readiness toolkit:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Practice that looks like the real thing.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {outcomes.map((metric) => (
            <Paper key={metric.label} elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h3" sx={{ color: BRAND_GREEN, fontWeight: 800, mb: 1, fontSize: '2rem' }}>
                {metric.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#333', mb: 1 }}>{metric.label}</Typography>
              <Typography sx={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.5, mt: 'auto' }}>
                {metric.detail}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Compliance & Quality */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: { xs: 4, md: 5 }, borderRadius: 3, borderTop: `6px solid ${BRAND_BLUE}` }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <VerifiedUser sx={{ fontSize: 48, color: BRAND_BLUE, mb: 2 }} />
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
                Practice Built on Real Survey Methodology
              </Typography>
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.6 }}>
                CareAscendium supports a disciplined internal survey exercise program:
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0 }}>
              {complianceItems.map((item) => (
                <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2 }} />
                  <Typography sx={{ color: '#444', fontWeight: 500, lineHeight: 1.5 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === index}
                onChange={handleChange(index)}
                sx={{
                  mb: 2.5,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 3, pt: 0 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Rehearse the Survey Before It Happens?
            </Typography>
            <Typography sx={{ mb: 4, fontSize: '1.1rem', maxWidth: 700, mx: 'auto', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              See how CareAscendium helps SNFs run scheduled, methodical mock surveys that mirror the real survey process—scope, sample, team, and pathways.
            </Typography>
            <Box>
              <Link href="/contact" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: BRAND_GREEN,
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#55901d' },
                  }}
                >
                  Request a Demo
                </Button>
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
